import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "progress", "preview", "button", "error"]
  static values = { 
    maxFiles: { type: Number, default: 1 },
    maxSize: { type: Number, default: 25485760 }, // 10MB
    exampleId: String,
    mediaType: String,
    containerTarget: String
  }

  connect() {
    this.acceptedTypes = ["image/", "video/"]
    // listen ActiveStorage direct-upload events bubbled from file input
    this.directUploadProgressHandler = this.handleDirectUploadProgress.bind(this)
    this.directUploadErrorHandler = this.handleDirectUploadError.bind(this)
    this.directUploadEndHandler = this.handleDirectUploadEnd.bind(this)
    // We will attach listeners to the element (form) so we get events for direct uploads
    this.element.addEventListener('direct-upload:progress', this.directUploadProgressHandler)
    this.element.addEventListener('direct-upload:error', this.directUploadErrorHandler)
    this.element.addEventListener('direct-upload:end', this.directUploadEndHandler)
  }

  highlight(e) {
    e.preventDefault()
    e.stopPropagation()
    this.element.querySelector('.upload-zone').classList.add('border-primary', 'bg-light')
  }

  unhighlight(e) {
    e.preventDefault()
    e.stopPropagation()
    this.element.querySelector('.upload-zone').classList.remove('border-primary', 'bg-light')
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.unhighlight(e)
    const dt = e.dataTransfer
    const files = dt.files
    this.handleFiles(files)
  }

  selectFiles() {
    console.log('selectFiles called');
    if (this.hasInputTarget) {
      this.inputTarget.click()
    } else {
      console.error("No input target defined for file selection")
    }
  }

  handleFileSelect(event) {
    this.handleFiles(event.target.files)
  }

  handleFiles(files) {
    this.clearError()
    
    // Validate files
    const validation = this.validateFiles(files)
    if (!validation.valid) {
      this.showError(validation.error)
      return
    }

    // Upload files
    this.uploadFiles([...files])
  }

  validateFiles(files) {
    // Check number of files
    if (files.length > this.maxFilesValue) {
      return {
        valid: false,
        error: this.data.get('maxFilesError') || `Maximum ${this.maxFilesValue} files allowed`
      }
    }

    // Check file types and sizes
    for (let file of files) {
      // Check file type
      if (!this.acceptedTypes.some(type => file.type.includes(type))) {
        return {
          valid: false,
          error: this.data.get('fileTypeError') || 'Invalid file type'
        }
      }

      // Check file size
      if (file.size > this.maxSizeValue) {
        return {
          valid: false,
          error: this.data.get('fileSizeError') || 'File too large'
        }
      }
    }

    return { valid: true }
  }

  async uploadFiles(files) {
    this.showProgress()

    // We handle direct upload to S3 using ActiveStorage DirectUpload
    const file = files[0]
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')

    // Get direct upload URL from the file input or default to Rails path
    const directUploadUrl = this.hasInputTarget && this.inputTarget.dataset.directUploadUrl ? this.inputTarget.dataset.directUploadUrl : '/rails/active_storage/direct_uploads'

    const upload = new DirectUpload(file, directUploadUrl, this)

    upload.create(async (error, blob) => {
      if (error) {
        this.handleUploadError(error)
        return
      }

      // After direct upload is done, we will attach the signed_id to the server resource
      // Hidden input name will match file input name (ex: 'media') so the server finds signed_id
      const inputName = this.hasInputTarget ? (this.inputTarget.getAttribute('name') || 'media') : 'media'
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = inputName
      hiddenField.value = blob.signed_id
      this.element.appendChild(hiddenField)

      // Send the signed_id to server to attach to a model/record
      const formData = new FormData()
      formData.append(inputName, blob.signed_id)
      formData.append('example_id', this.exampleIdValue)

      try {
        const response = await fetch(this.element.action, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        })

        if (response.ok) {
          const result = await response.json()
          this.handleUploadSuccess(result)
        } else {
          const errorData = await response.json()
          this.handleUploadError(errorData.message || 'Upload failed')
        }
      } catch (error) {
        this.handleUploadError('Network error')
      }
    })
  }

  // ActiveStorage direct upload progress handler
  handleDirectUploadProgress(event) {
    const { id, file, progress } = event.detail
    if (this.hasProgressTarget) {
      // Keep progress simple: show spinner and percent
      const percent = Math.round(progress)
      this.progressTarget.classList.remove('d-none')
      // Prefer small textual percent inside spinner area
      const smallText = this.progressTarget.querySelector('small')
      if (smallText) smallText.textContent = `${percent}%`
    }
  }

  handleDirectUploadError(event) {
    const { file, error } = event.detail
    this.handleUploadError(error || 'Upload error')
  }

  handleDirectUploadEnd(event) {
    // Hide progress on end
    if (this.hasProgressTarget) this.progressTarget.classList.add('d-none')
  }

  // Delegate called by Active Storage DirectUpload
  directUploadWillStoreFileWithXHR(xhr) {
    // Attach a progress handler so we can show % to the user
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && this.hasProgressTarget) {
        const percent = Math.round((e.loaded / e.total) * 100)
        const smallText = this.progressTarget.querySelector('small')
        if (smallText) smallText.textContent = `${percent}%`
      }
    })
  }

  directUploadWillCreateBlobWithXHR(xhr) {
    // optional: can set headers or read something from xhr
  }

  handleUploadSuccess(result) {
    this.hideProgress()
    
    // Show success message
    this.showSuccess(result.message || 'Successfully uploaded')
    
    // Update the container with new content
    if (result.preview_html && this.containerTargetValue) {
      const container = document.getElementById(this.containerTargetValue)
      if (container) {
        // Сохраняем ссылку на текущую форму перед обновлением
        const currentForm = this.element
        
        // Обновляем контейнер новым содержимым (превью)
        container.innerHTML = result.preview_html
        
        // Добавляем скрытую форму обратно для будущих замен
        const hiddenDiv = document.createElement('div')
        hiddenDiv.style.display = 'none'
        hiddenDiv.appendChild(currentForm)
        container.appendChild(hiddenDiv)
      }
    }
    
    // Clear input if exists
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }
  }

  handleUploadError(message) {
    this.hideProgress()
    this.showError(message)
  }

  addFilePreview(file) {
    if (!this.hasPreviewTarget) return
    
    const previewHtml = this.createPreviewHtml(file)
    this.previewTarget.insertAdjacentHTML('beforeend', previewHtml)
  }

  createPreviewHtml(file) {
    const isImage = file.content_type.startsWith('image/')
    const isVideo = file.content_type.startsWith('video/')
    
    let mediaHtml = ''
    if (isImage) {
      mediaHtml = `<img src="${file.url}" class="img-fluid rounded" alt="${file.filename}">`
    } else if (isVideo) {
      mediaHtml = `
        <video class="img-fluid rounded" controls>
          <source src="${file.url}" type="${file.content_type}">
        </video>
      `
    }
    
    return `
      <div class="col-md-4 mb-3" data-file-id="${file.id}">
        <div class="card">
          <div class="card-body p-2">
            ${mediaHtml}
            <div class="mt-2">
              <small class="text-muted">${file.filename}</small>
              <button type="button" class="btn btn-sm btn-outline-danger float-end" 
                      data-action="click->media-upload#removeFile" 
                      data-file-id="${file.id}">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    `
  }

  async removeFile(event) {
    const fileId = event.currentTarget.dataset.fileId
    const fileElement = document.querySelector(`[data-file-id="${fileId}"]`)
    
    if (!confirm(this.data.get('confirmRemove') || 'Are you sure?')) {
      return
    }
    
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      const response = await fetch(`/master/nominations/${fileId}/remove_media`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        fileElement.remove()
        this.showSuccess('File removed')
      } else {
        this.showError('Failed to remove file')
      }
    } catch (error) {
      this.showError('Network error')
    }
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('d-none')
    }
    // Hide upload zone content
    const uploadZone = this.element.querySelector('.upload-zone')
    const children = uploadZone.children
    for (let i = 0; i < children.length; i++) {
      if (!children[i].hasAttribute('data-media-upload-target')) {
        children[i].style.display = 'none'
      }
    }
  }

  hideProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('d-none')
    }
    // Show upload zone content again
    const uploadZone = this.element.querySelector('.upload-zone')
    const children = uploadZone.children
    for (let i = 0; i < children.length; i++) {
      if (!children[i].hasAttribute('data-media-upload-target')) {
        children[i].style.display = ''
      }
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('d-none')
    } else {
      // Fallback to alert if no error target
      alert(message)
    }
  }

  disconnect() {
    this.element.removeEventListener('direct-upload:progress', this.directUploadProgressHandler)
    this.element.removeEventListener('direct-upload:error', this.directUploadErrorHandler)
    this.element.removeEventListener('direct-upload:end', this.directUploadEndHandler)
  }

  showSuccess(message) {
    // You can implement a toast notification here
    // For now, we'll use a simple temporary alert
    const alert = document.createElement('div')
    alert.className = 'alert alert-success alert-dismissible fade show position-fixed'
    alert.style.cssText = 'top: 20px; right: 20px; z-index: 1050; min-width: 300px;'
    alert.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    document.body.appendChild(alert)
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove()
      }
    }, 3000)
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('d-none')
    }
  }
}
